import * as Immutable from 'immutable';
import { handleActions } from 'redux-actions';

import {
  publicFields,
  updateSlugs,
  userAccountStructure,
  userDataBooleanFields,
  UserEmploymentFields,
  UserFinanceFields,
} from '../../shared/data/User';
import { AccountDataFieldsValidation } from '../../shared/helpers/UserValidation';

import { AccountActionType, AccountStore, AccountSubmittedActionType, CountryOption } from './AccountReducer.types';
import { getNewState, reduceObjectMap, setAccountFieldData, setFieldValue } from './Helpers';
import { initialAccountData, initialBasicDataField, initialDataFieldEmployer, initialDataFieldEmployerFinance } from './InitialData';
import { ImmutableInterface } from '../../types';

const userValidation = new AccountDataFieldsValidation();

//Sorts values with matching title and value first.
//So that when selecting one of options with the same value, the right label appears.
export function sortCanonicalValuesFirst(value: Immutable.List<Immutable.Map<string, any>>) {
  return value.sort((x: any, y: any) => {
    if (!x || !y) {
      return 0;
    }
    if (x.get('value') === x.get('label')) {
      if (y.get('value') === y.get('label')) {
        return x.get('label').localeCompare(y.get('label'));
      }
      return -1;
    }
    if (y.get('value') === y.get('label')) {
      return 1;
    }

    return x.get('label').localeCompare(y.get('label'));
  });
}

export const orderMetadataDropdown = (metadata: any) =>
  metadata.map((value: any, key: string) => {
    if (key !== 'graduationYear' && key !== 'defaultCountry') {
      return sortCanonicalValuesFirst(value);
    } else {
      return value;
    }
  });

const AccountReducer = handleActions<any>(
  {
    REQUEST_ACCOUNT: (state: AccountStore) => state.set('isFetching', true),
    RESPONSE_COMPETITION_SUCCESS: (state: AccountStore, action: AccountActionType) => state.set('competition', action.payload.json),

    SET_FORM_STATE: (state: AccountStore, action: AccountActionType) =>
      state.set('currentForm', action.payload.data.currentForm).set('accountType', action.payload.data.accountType),

    RESPONSE_ACCOUNT: (state: AccountStore, action: AccountSubmittedActionType) =>
      state
        .set('isFetching', false)
        .set('submitted', true)
        .set('locked', action.payload.locked)
        .set('approved', action.payload.approved)
        .set('dataFields', Immutable.fromJS({ ...initialBasicDataField, ...initialDataFieldEmployer, ...initialDataFieldEmployerFinance })),

    RESPONSE_ACCOUNT_FAILURE: (state: AccountStore, action: AccountSubmittedActionType) => {
      let newState = state;
      if (action.payload) {
        const errors = reduceObjectMap(action.payload);
        Object.keys(errors).map((key: string) => {
          newState = newState.setIn(['dataFields', key, 'errors'], Immutable.fromJS(errors[key]));
          newState = newState.setIn(['dataFields', key, 'valid'], false);
        });
      }

      updateSlugs.forEach((slug: string) => {
        const fields = state.getIn(['updateForm', slug, 'fields']);
        // @ts-ignore
        const valid = userValidation.createValidation(newState.get('dataFields'), fields);
        newState = newState.setIn(['updateForm', slug, 'valid'], valid);
      });

      return newState
        .set('isFetching', false)
        .setIn(['dataFields', 'captcha', 'value'], '')
        .setIn(['dataFields', 'captcha', 'valid'], false)
        .set('isValidToSubmit', false);
    },

    RESPONSE_ACCOUNT_FAIL: (state: AccountStore) => state.set('isFetching', false),

    REQUEST_USER_OPTIONS: (state: AccountStore) => state.set('isFetchUserOptions', true),

    REQUEST_TOKEN: (state: AccountStore) => state.set('fetchingToken', true),

    CLEAN_TOKEN: (state: AccountStore) =>
      state.setIn(['dataFields', 'accessToken'], Immutable.fromJS({ value: '', valid: true, errors: [] })),

    RESPONSE_TOKEN_FAIL: (state: AccountStore) =>
      state
        .set('fetchingToken', false)
        .setIn(['dataFields', 'accessTokenPassword'], Immutable.fromJS({ value: '', valid: true, errors: [] }))
        .set(
          'dirtyFields',
          state.get('dirtyFields').filter((dirtyFields: string) => dirtyFields !== 'accessTokenPassword')
        ),

    ACCOUNT_GENERATE_ACCESS_TOKEN: (state: AccountStore, action: AccountActionType) =>
      state
        .set('fetchingToken', false)
        .setIn(['dataFields', 'accessTokenPassword'], Immutable.fromJS({ value: '', valid: true, errors: [] }))
        .setIn(['dataFields', 'accessToken'], Immutable.fromJS({ value: action.payload.data, valid: true, errors: [] }))
        .set(
          'dirtyFields',
          state.get('dirtyFields').filter((dirtyFields: string) => dirtyFields !== 'accessTokenPassword')
        ),

    CLEAN_WORKDAY: (state: AccountStore, action: AccountActionType) =>
      state
        .set('fetchingToken', false)
        .setIn(['dataFields', 'workdayPassword'], Immutable.fromJS({ value: '', valid: true, errors: [] }))
        .set(
          'dirtyFields',
          state.get('dirtyFields').filter((dirtyFields: string) => dirtyFields !== 'workdayPassword')
        ),

    CLEAN_BRAIN_LABS: (state: AccountStore, action: AccountActionType) =>
      state
        .set('fetchingToken', false)
        .setIn(['dataFields', 'brainLabsPassword'], Immutable.fromJS({ value: '', valid: true, errors: [] }))
        .set(
          'dirtyFields',
          state.get('dirtyFields').filter((dirtyFields: string) => dirtyFields !== 'brainLabsPassword')
        ),
    UPDATE_RECERTIFICATION_PROGRESS: (state: AccountStore, action: AccountActionType) =>
      state.set('recertificationProgress', state.get('recertificationProgress').concat(action.payload)),

    ClEAN_RECERTIFICATION_PROGRESS: (state: AccountStore) => state.set('recertificationProgress', Immutable.List()),

    ACCOUNT_CHANGE_EMAIL: (state: AccountStore, action: AccountActionType) =>
      state
        .set('isFetching', false)
        .setIn(['dataFields', 'changeEmailPassword'], Immutable.fromJS({ value: '', valid: false, errors: [] }))
        .setIn(['dataFields', 'changeEmail'], Immutable.fromJS({ value: '', valid: false, errors: [] }))
        .setIn(['changeEmailForm', 'changeEmail', 'valid'], false)
        .set('changedEmail', true)
        .set('accountVerifyEmail500', false)
        .set('unverifiedEmail', action.payload),

    CLEAN_CHANGE_EMAIL: (state: AccountStore) =>
      state
        .setIn(['dataFields', 'changeEmailPassword'], Immutable.fromJS({ value: '', valid: false, errors: [] }))
        .setIn(['dataFields', 'changeEmail'], Immutable.fromJS({ value: '', valid: false, errors: [] }))
        .setIn(['changeEmailForm', 'changeEmail', 'valid'], false),

    TOGGLE_ACCOUNT_DETAILS_AGREE: (state: AccountStore, action: AccountActionType) => state.set('isAccountDetailsAgree', action.payload),

    TOGGLE_IS_AGREED_ALPHATHON_UPDATE_ACCOUNT_DETAILS: (state: AccountStore, action: AccountActionType) =>
      state.set('isAgreedAlphathonUpdateAccountDetails', action.payload),

    RESPONSE_USER_OPTIONS: (state: AccountStore, action: AccountActionType) => {
      let metadata = orderMetadataDropdown(action.payload.data);

      // Put the default country (detected based on IP Geolocation) on the top of the countries list
      if (metadata.get('country') && metadata.get('defaultCountry')) {
        const country = metadata.get('country');
        const defaultCountryIdx = country.findIndex(
          (option: ImmutableInterface<CountryOption>) => option.get('value') === metadata.get('defaultCountry')
        );
        if (defaultCountryIdx) {
          const defaultCountryOption = country.get(defaultCountryIdx);
          metadata = metadata.set('country', country.delete(defaultCountryIdx).unshift(defaultCountryOption));
        }
      }

      return state
        .set('hasUserOptions', true)
        .set('metadata', metadata)
        .set('userOptionsFailure', false)
        .set('isFetchUserOptions', false)
        .set(
          'countryOptionsMap',
          Immutable.fromJS(action.payload.data.toJS().country.reduce((obj: any, item: any) => ({ ...obj, [item.value]: item.label }), {}))
        );
    },
    RESPONSE_USER_OPTIONS_FAILURE: (state: AccountStore) => state.set('isFetchUserOptions', false).set('userOptionsFailure', true),

    REQUEST_ACCOUNT_VERIFY_EMAIL: (state: AccountStore) => state.set('isFetching', true),
    ACCOUNT_VERIFY_EMAIL500: (state: AccountStore) => state.set('accountVerifyEmail500', true),

    RESPONSE_ACCOUNT_VERIFY_EMAIL: (state: AccountStore, action: AccountActionType) => {
      const verifiedType = action.payload.detail ? action.payload.detail : 'EMAIL_VERIFIED';

      return state.merge({
        isFetching: false,
        verified: true,
        verifiedType,
        changedEmail: false,
        accountVerifyEmail500: false,
        unverifiedEmail: '',
      });
    },

    RESPONSE_ACCOUNT_VERIFY_EMAIL_FAILURE: (state: AccountStore) => state.set('isFetching', false),

    SET_REFERRER: (state: AccountStore) => state.set('referrer', true),

    ACCOUNT_TERMS_ACCEPTED: (state: AccountStore, action: AccountActionType) => state.set('termsAccepted', action.payload.data),

    ACCOUNT_VALID_TO_SUBMIT: (state: AccountStore, action: AccountActionType) => state.set('isValidToSubmit', action.payload.data),

    ACCOUNT_CHANGE_TERMS: (state: AccountStore, action: AccountActionType) =>
      state.setIn(['terms', action.payload.field], action.payload.data),

    ACCOUNT_CHANGE_METADATA: (state: AccountStore, action: AccountActionType) =>
      state.setIn(['metadata', 'university'], Immutable.fromJS(action.payload.data)),

    CLEAN_ACCOUNT: (state: AccountStore) =>
      state.merge(
        Immutable.fromJS({
          ...initialAccountData.toJS(),
          submitted: state.get('submitted'),
          recertificationProgress: Immutable.List(),
        })
      ),

    CHANGE_FIELD_VALUES_ACCOUNT: (state: AccountStore, action: AccountActionType) => {
      if (action.payload.data !== undefined) {
        const field = action.payload.data.name;
        const value = setFieldValue(action.payload.data);

        return state.setIn(['dataFields', field, 'value'], value);
      } else {
        return state;
      }
    },

    EDIT_ACCOUNT_DATA: (state: AccountStore) => state.set('isFetching', false),

    DISABLE_ACCOUNT_FINISHED: (state: AccountStore) => state.set('isFetching', false),

    SAVE_USER_DATA: (state: AccountStore, action: any) => {
      const data = Immutable.fromJS(action.payload.json);
      const options = Immutable.fromJS({
        competition: action.payload.competition,
        countriesOptions: state.getIn(['account', 'countryOptionsMap']),
      });
      const { resume, ...userData } = Object.assign({}, userAccountStructure, { email: ['email'] });
      let dataFields = state.get('dataFields');
      const employment = data.get('employment') === null ? false : data.get('employment');

      Object.keys(userData).forEach((k: string) => {
        if (k === 'presently_employed') {
          // Check if  presently_employed field value and set its
          const value = employment === false ? false : true;
          dataFields = dataFields.setIn([k, 'value'], value).setIn([k, 'valid'], true);
        } else if (UserFinanceFields.includes(k) && employment && !data.getIn(['employment', 'finance'])) {
          // Check if employment finance Field is true and set employment finance Fields with no errors
          const value = userDataBooleanFields.includes(k) ? null : '';
          dataFields = dataFields.setIn([k, 'value'], value).setIn([k, 'valid'], false);
        } else if (UserEmploymentFields.includes(k) && employment === false) {
          // Check if employment is false set employment Fields with no errors
          const value = userDataBooleanFields.includes(k) ? null : '';
          dataFields = dataFields.setIn([k, 'value'], value).setIn([k, 'valid'], false);
        } else {
          const dataUser: any = userData;
          let value = data.getIn(dataUser[k]);
          value =
            !userDataBooleanFields.includes(k) && (value === null || value === undefined)
              ? ''
              : k === 'graduationYear'
              ? // @ts-ignore
                value.toString()
              : value;
          // @ts-ignore
          dataFields = dataFields.set(
            k,
            Immutable.fromJS(
              setAccountFieldData({
                userValidation: userValidation,
                field: k,
                value: value as string | number | boolean,
                accountState: dataFields,
                options: options,
              })
            ).get(k)
          );
        }
      });

      let newState = state.set('dataFields', dataFields);

      updateSlugs.forEach((slug: string) => {
        const fields = state.getIn(['updateForm', slug, 'fields']);
        // @ts-ignore
        const valid = userValidation.createValidation(newState.get('dataFields'), fields);
        newState = newState.setIn(['updateForm', slug, 'valid'], valid);
      });

      // The /users/self response now nests `unverifiedEmail` inside the
      // `auxiliary` property (mirroring the create-user / PATCH /users/self
      // payload shape). Fall back to the legacy root-level field for backward
      // compatibility with any other code paths that still send it there.
      const unverifiedEmail = data.getIn(['auxiliary', 'unverifiedEmail']) || data.get('unverifiedEmail') || '';

      return newState
        .set('dirtyFields', Immutable.fromJS([]))
        .set('isAccountDetailsAgree', false)
        .set('isAgreedAlphathonUpdateAccountDetails', false)
        .set('unverifiedEmail', unverifiedEmail);
    },

    UPDATE_CONSULTANT_REQUIRED_FIELDS: (state: AccountStore, action: any) => {
      let dataFields = state.get('dataFields');
      ['major', 'degree', 'graduationYear'].forEach((k: string) => {
        let value = dataFields.get(k).get('value');
        value =
          !userDataBooleanFields.includes(k) && (value === null || value === undefined)
            ? ''
            : k === 'graduationYear'
            ? // @ts-ignore
              value.toString()
            : value;
        dataFields = dataFields.set(
          k,
          Immutable.fromJS(
            setAccountFieldData({
              userValidation: userValidation,
              field: k,
              value: value,
              accountState: dataFields,
              validationOptions: { isConsultant: true },
            })
          ).get(k)
        );
      });

      return state.set('dataFields', dataFields);
    },

    ACCOUNT_CHANGE_FIELD_CONNECT_SAVED: (state: AccountStore, action: AccountActionType) => {
      if (action.payload.data !== undefined) {
        // The field Name an its Value
        const id = action.payload.data.currentTarget ? action.payload.data.currentTarget.name : action.payload.data.name;
        const value = action.payload.data.currentTarget ? action.payload.data.currentTarget.value : action.payload.data.value;

        if (state.get('accountType') === 'createForm') {
          return getNewState(action.payload.data, state, userValidation);
        } else {
          const currentFieldSaved = action.payload.currentFieldSaved;
          const fieldsExcludeOfdirtyFields = Immutable.List(['changeEmail', 'changeEmailPassword']);
          let dirtyFields = state.get('dirtyFields');
          let isAccountDetailsAgree = state.get('isAccountDetailsAgree');
          if (value === currentFieldSaved) {
            dirtyFields = dirtyFields.filter((field: string) => field !== id);
          } else if (!dirtyFields.find((field: string) => field === id) && !fieldsExcludeOfdirtyFields.includes(id)) {
            dirtyFields = dirtyFields.push(id);
          }
          if (dirtyFields.size === 0) {
            isAccountDetailsAgree = false;
          }

          return getNewState(action.payload.data, state, userValidation)
            .set('dirtyFields', dirtyFields)
            .set('isAccountDetailsAgree', isAccountDetailsAgree);
        }
      } else {
        return state;
      }
    },

    UNDO_EDIT_USER_DATA: (state: AccountStore, action: AccountActionType) => {
      const data = reduceObjectMap(action.payload.data);
      const isConsultant = action.payload.isConsultant;
      let dataFields = state.get('dataFields').map((fieldData: any, field: string) => {
        if (data.hasOwnProperty(field)) {
          return fieldData.set('value', data[field]).set('errors', Immutable.List());
        } else {
          return fieldData;
        }
      });

      const options = Immutable.fromJS({
        countriesOptions: state.getIn(['account', 'countryOptionsMap']),
        isConsultant: isConsultant,
      });
      const dataToValidate = Immutable.fromJS(action.payload.data);
      const { resume, ...userData } = Object.assign({}, userAccountStructure, { email: ['email'] });
      const employment = dataToValidate.get('presently_employed') === false ? false : dataToValidate.get('employment');
      Object.keys(userData).forEach(k => {
        if (k === 'presently_employed') {
          // Check if  presently_employed field value and set its
          const value = employment === false ? false : true;
          dataFields = dataFields.setIn([k, 'value'], value).setIn([k, 'valid'], true);
        } else if (UserFinanceFields.includes(k) && employment && !dataToValidate.getIn(['employment', 'finance'])) {
          // Check if employment finance Field is true and set employment finance Fields with no errors
          const value = userDataBooleanFields.includes(k) ? null : '';
          dataFields = dataFields.setIn([k, 'value'], value).setIn([k, 'valid'], false);
        } else if (UserEmploymentFields.includes(k) && employment === false) {
          // Check if employment is false set employment Fields with no errors
          const value = userDataBooleanFields.includes(k) ? null : '';
          dataFields = dataFields.setIn([k, 'value'], value).setIn([k, 'valid'], false);
        } else if (publicFields.includes(k as keyof typeof userAccountStructure)) {
          let value = dataToValidate.getIn(userData[k as keyof typeof userData]);
          dataFields = dataFields.setIn([k, 'value'], value).setIn([k, 'valid'], true);
        } else {
          const dataUser: any = userData;
          let value = dataToValidate.getIn(dataUser[k]);
          value =
            !userDataBooleanFields.includes(k) && (value === null || value === undefined)
              ? ''
              : k === 'graduationYear'
              ? // @ts-ignore
                value.toString()
              : value;
          // @ts-ignore
          dataFields = dataFields.set(
            k,
            Immutable.fromJS(
              setAccountFieldData({
                userValidation: userValidation,
                field: k,
                value: value as string | number | boolean,
                accountState: dataFields,
                options: options,
              })
            ).get(k)
          );
        }
      });

      let newState = state.set('dataFields', dataFields);

      updateSlugs.forEach((slug: string) => {
        const fields = state.getIn(['updateForm', slug, 'fields']);
        // @ts-ignore
        const valid = userValidation.createValidation(newState.get('dataFields'), fields);
        newState = newState.setIn(['updateForm', slug, 'valid'], valid);
      });

      return state.merge(
        Immutable.fromJS({
          isFetching: false,
          dataFields,
          dirtyFields: Immutable.List(),
          isAccountDetailsAgree: false,
          isAgreedAlphathonUpdateAccountDetails: false,
        })
      );
    },
    SET_PHOTO_DIRTY: (state: AccountStore) => state.set('dirtyFields', state.get('dirtyFields').push('photo')),
    SET_RESUME_DIRTY: (state: AccountStore) => state.set('dirtyFields', state.get('dirtyFields').push('resume')),
    ACCOUNT_CHANGE_FIELD: (state: AccountStore, action: AccountActionType) => {
      const dataFields = state.get('dataFields');
      if (action.payload.field === 'presently_employed' && action.payload.data[action.payload.field].value === false) {
        return state.merge(
          Immutable.fromJS({
            dataFields: dataFields.merge(
              Immutable.fromJS({
                ...action.payload.data,
                ...initialDataFieldEmployer,
                ...initialDataFieldEmployerFinance,
              })
            ),
          })
        );
      } else if (
        action.payload.field === 'finance' &&
        action.payload.data[action.payload.field].value === false &&
        state.getIn(['dataFields', 'presently_employed', 'value']) === true
      ) {
        return state.merge(
          Immutable.fromJS({
            dataFields: dataFields.merge(
              Immutable.fromJS({
                ...action.payload.data,
                ...initialDataFieldEmployerFinance,
              })
            ),
          })
        );
      } else {
        return state.merge(Immutable.fromJS({ dataFields: dataFields.merge(Immutable.fromJS(action.payload.data)) }));
      }
    },
    AUTHENTICATION_LOGOUT: () => initialAccountData,
  },
  initialAccountData
);

export default AccountReducer;
